import {
  AHText,
  AHTextFonts,
  AHWidgetNoData,
  AHWidgetTitle,
  Box,
  TouchableBox,
  useTime,
} from "arkh";
import { DateTime } from "luxon";
import React, { useCallback, useMemo } from "react";
import { BaseWidgetProps } from "../BaseProps";
import { EmptyResult } from "../components/EmptyResult";
import { ResultGrid } from "../components/ResultGrid";
import { graphql, useFragment } from "react-relay";
import { WidgetRoutinePositivePercentageRoutineItem_routine$key } from "./__generated__/WidgetRoutinePositivePercentageRoutineItem_routine.graphql";
import { WidgetRoutinePositivePercentage_widgetRoutinePositivePercentage$key } from "./__generated__/WidgetRoutinePositivePercentage_widgetRoutinePositivePercentage.graphql";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";
import { WidgetRoutinePositivePercentageSubscription } from "./__generated__/WidgetRoutinePositivePercentageSubscription.graphql";
import { ResultRow } from "../WidgetTaskCollection/ResultRow";
import { useSetWidgetFocus } from "../useSetFocus";

function ResultPercentageC({
  percentage,
  stale = false,
}: {
  percentage: number | null;
  stale?: boolean;
}) {
  return percentage === null ? (
    <EmptyResult size="small" />
  ) : (
    <AHText
      type="subtitle"
      font={AHTextFonts.BOLDER}
      color={percentage >= 80 ? "positive" : "negative"}
      style={{
        opacity: stale ? 0.6 : 1,
      }}
    >
      {`${percentage}%`}
    </AHText>
  );
}
const ResultPerentage = React.memo(ResultPercentageC);

export const WidgetRoutinePositivePercentage = ({
  widget: widgetKey,
  onEvent,
  isFocused,
  storecastId,
  mode,
  interactive,
}: {
  widget: WidgetRoutinePositivePercentage_widgetRoutinePositivePercentage$key;
} & BaseWidgetProps) => {
  const widget = useFragment(
    graphql`
      fragment WidgetRoutinePositivePercentage_widgetRoutinePositivePercentage on WidgetRoutinePositivePercentage {
        id
        title
        routines {
          id
          ...WidgetRoutinePositivePercentageRoutineItem_routine
        }
      }
    `,
    widgetKey
  );
  const routines = useMemo(
    () => widget.routines?.slice(0, 8) ?? [],
    [widget.routines]
  );
  type RoutineItem = (typeof routines)[number];
  const setFocus = useSetWidgetFocus(widget.id, storecastId);
  const onPress = useCallback(() => {
    if (mode === "FOCUS" && !isFocused) {
      setFocus();
    } else {
      onEvent?.({
        kind: "WidgetRoutinePositivePercentage",
        widgetId: widget.id,
      });
    }
  }, [isFocused, mode, onEvent, setFocus, widget.id]);

  useSubscriptionWithSkip<WidgetRoutinePositivePercentageSubscription>(
    useMemo(
      () => ({
        variables: { routineIds: widget.routines.map((r) => r.id) },
        subscription: graphql`
          subscription WidgetRoutinePositivePercentageSubscription(
            $routineIds: [ID!]!
          ) {
            routineTaskExecutionChanged(routineIds: $routineIds) {
              id
              routineOccasion {
                id
                routine {
                  id
                  ...WidgetRoutinePositivePercentageRoutineItem_routine
                }
              }
            }
          }
        `,
      }),
      [widget.routines]
    ),
    {
      skip: interactive === false,
    }
  );

  const renderRow = useCallback(
    (routine: RoutineItem) => (
      <WidgetRoutinePositivePercentageRoutineItem routine={routine} />
    ),
    []
  );
  const keyExtractor = useCallback((routine: RoutineItem) => {
    return routine.id;
  }, []);

  if (routines.length === 0) {
    return (
      <Box style={{ flex: 1 }} color="surface">
        <AHWidgetTitle>{widget.title}</AHWidgetTitle>
        <AHWidgetNoData />
      </Box>
    );
  }

  return (
    <TouchableBox style={{ flex: 1 }} color="surface" onPress={onPress}>
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      <ResultGrid
        items={routines}
        keyExtractor={keyExtractor}
        renderRow={renderRow}
      />
    </TouchableBox>
  );
};

const min = 60000; // 1 minute
function WidgetRoutinePositivePercentageRoutineItem(props: {
  routine: WidgetRoutinePositivePercentageRoutineItem_routine$key;
}) {
  const routine = useFragment(
    graphql`
      fragment WidgetRoutinePositivePercentageRoutineItem_routine on Routine {
        id
        title
        ownerTeam {
          name
        }
        lastCompletedOccasion {
          endAt
          percentage
        }
      }
    `,
    props.routine
  );

  const now = useTime(min);

  const endAt = useMemo(
    () =>
      routine.lastCompletedOccasion?.endAt
        ? DateTime.fromISO(routine.lastCompletedOccasion.endAt)
        : undefined,
    [routine.lastCompletedOccasion?.endAt]
  );
  const isStale = endAt ? now > endAt : true;

  return (
    <ResultRow
      title={routine.title}
      subTitle={routine.ownerTeam?.name ?? ""}
      stale={isStale}
    >
      <ResultPerentage
        percentage={routine.lastCompletedOccasion?.percentage ?? null}
        stale={isStale}
      />
    </ResultRow>
  );
}
